import React, { useState, useEffect } from 'react';
import Logo from '../components/common/Logo';
import { Calendar, User, Check, X, FileText, RefreshCw, Trash2, Video } from 'lucide-react';
import DoctorSidebar from '../components/dashboard-doctor/DoctorSidebar';
import AppointmentService from '../services/appoinment.service';
import SurveyService from '../services/survey.service';
import { differenceInDays, isAfter, isBefore, isEqual, format } from 'date-fns';
import PropTypes from 'prop-types';
import { getStatusLabel, getStatusColor } from '../utils/appointmentUtils';
import RescheduleAppointmentModal from '../components/doctor/RescheduleAppointmentModal';
import DeleteAppointmentModal from '../components/doctor/DeleteAppointmentModal';
import UpdateAppointmentModal from '../components/doctor/UpdateAppointmentModal';

const DoctorAppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modifica dei filtri: solo intervallo di date
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');
  
  // Stati per il modale delle risposte al questionario
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [surveyError, setSurveyError] = useState(null);
  
  // Stati per la modale di rifissaggio appuntamento
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // All'inizio del componente, aggiungi questi stati
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [appointmentToUpdate, setAppointmentToUpdate] = useState(null);

  // Aggiungi queste funzioni per gestire l'apertura e la chiusura delle modali
  const handleOpenDeleteModal = (appointment) => {
    setAppointmentToDelete(appointment);
    setShowDeleteModal(true);
  };

  const handleDeleteSuccess = () => {
    setShowDeleteModal(false);
    loadAppointments(); // Ricarica gli appuntamenti
  };

  const handleOpenUpdateModal = (appointment) => {
    setAppointmentToUpdate(appointment);
    setShowUpdateModal(true);
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    loadAppointments(); // Ricarica gli appuntamenti
  };

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      
      console.log('Sto per chiamare getDoctorAppointments()');
      const response = await AppointmentService.getDoctorAppointments();
      
      // Assicurati che i dati siano un array
      const appointmentsArray = Array.isArray(response) ? response : (Array.isArray(response.data) ? response.data : []);
      console.log('Array finale degli appuntamenti:', appointmentsArray);
      
      setAppointments(appointmentsArray);
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento degli appuntamenti:', err);
      setError('Non è stato possibile caricare gli appuntamenti. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Funzione per caricare le risposte al Questionario di un paziente
  const loadPatientSurvey = async (patientId) => {
    try {
      setLoadingSurvey(true);
      setSurveyError(null);
      
      console.log(`Caricamento risposte al questionario per paziente #${patientId}`);
      const responses = await SurveyService.getUserResponses(patientId);
      
      setSurveyResponses(responses);
      setSelectedPatient(patientId);
      setShowSurveyModal(true);
    } catch (err) {
      console.error('Errore nel caricamento delle risposte al questionario:', err);
      setSurveyError('Non è stato possibile caricare le risposte al questionario del paziente.');
    } finally {
      setLoadingSurvey(false);
    }
  };
  
  // Funzione per aprire il modale di rifissaggio appuntamento
  const handleRescheduleAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowRescheduleModal(true);
  };
  
  // Funzione per eliminare un appuntamento
  const handleDeleteAppointment = async (appointmentId) => {
    if (window.confirm("Sei sicuro di voler eliminare questo appuntamento?")) {
      try {
        await AppointmentService.delete(appointmentId);
        loadAppointments(); // Ricarica appuntamenti
      } catch (err) {
        console.error('Errore nell\'eliminazione dell\'appuntamento:', err);
        alert('Non è stato possibile eliminare l\'appuntamento. Riprova più tardi.');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (dateString) => {
    const appointmentDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    appointmentDate.setHours(0, 0, 0, 0);
    return differenceInDays(appointmentDate, today);
  };

  const handleCompleteAppointment = async (appointmentId) => {
    if (window.confirm("Sei sicuro di voler segnare questo appuntamento come completato?")) {
      try {
        await AppointmentService.updateStatus(appointmentId, 'completed');
        loadAppointments(); // Ricarica appuntamenti
      } catch (err) {
        console.error('Errore nel completamento dell\'appuntamento:', err);
        alert('Non è stato possibile completare l\'appuntamento. Riprova più tardi.');
      }
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (window.confirm("Sei sicuro di voler annullare questo appuntamento?")) {
      try {
        await AppointmentService.updateStatus(appointmentId, 'cancelled');
        loadAppointments(); // Ricarica appuntamenti
      } catch (err) {
        console.error('Errore nell\'annullamento dell\'appuntamento:', err);
        alert('Non è stato possibile annullare l\'appuntamento. Riprova più tardi.');
      }
    }
  };

  // Filtra gli appuntamenti "suggested"
  const suggestedAppointments = appointments
    .filter(appointment => appointment.status === 'suggested')
    .sort((a, b) => {
      // Ordina per data e ora, prima i più vicini
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA.getTime() - dateB.getTime();
    });

  // Filtra gli appuntamenti "upcoming" (escludendo i "suggested")
  const upcomingAppointments = appointments
    .filter(appointment => {
      const daysRemaining = getDaysRemaining(appointment.date);
      // Includi appuntamenti futuri o di oggi che non sono ancora completati, cancellati o suggeriti
      return daysRemaining >= 0 && 
             appointment.status !== 'completed' && 
             appointment.status !== 'cancelled' &&
             appointment.status !== 'suggested';
    })
    .sort((a, b) => {
      // Ordina per data e ora, prima i più recenti
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA.getTime() - dateB.getTime();
    });

  // Filtra gli appuntamenti "past" (escludendo i "suggested")
  const pastAppointments = appointments
    .filter(appointment => {
      const daysRemaining = getDaysRemaining(appointment.date);
      // Includi appuntamenti passati o già completati/cancellati, ma non suggeriti
      return (daysRemaining < 0 || 
              appointment.status === 'completed' || 
              appointment.status === 'cancelled') &&
              appointment.status !== 'suggested';
    })
    .sort((a, b) => {
      // Ordina per data e ora, prima i più recenti
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateB.getTime() - dateA.getTime(); // Ordine decrescente
    });

  // Filtra gli appuntamenti per intervallo di date
  const filterAppointmentsByDateRange = (appointments) => {
    if (!startDateFilter && !endDateFilter) return appointments;
    
    return appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.date);
      appointmentDate.setHours(0, 0, 0, 0);
      
      let isInRange = true;
      
      if (startDateFilter) {
        const startDate = new Date(startDateFilter);
        startDate.setHours(0, 0, 0, 0);
        isInRange = isInRange && (isAfter(appointmentDate, startDate) || isEqual(appointmentDate, startDate));
      }
      
      if (endDateFilter) {
        const endDate = new Date(endDateFilter);
        endDate.setHours(0, 0, 0, 0);
        isInRange = isInRange && (isBefore(appointmentDate, endDate) || isEqual(appointmentDate, endDate));
      }
      
      return isInRange;
    });
  };

  // Applica il filtro dell'intervallo di date
  const filteredUpcomingAppointments = filterAppointmentsByDateRange(upcomingAppointments);
  const filteredPastAppointments = filterAppointmentsByDateRange(pastAppointments);
  const filteredSuggestedAppointments = filterAppointmentsByDateRange(suggestedAppointments);

  // Resetta i filtri
  const handleResetFilters = () => {
    setStartDateFilter('');
    setEndDateFilter('');
  };

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <DoctorSidebar />
      </div>

      <div className="flex-1 bg-[#FAF4EB] p-8">
        <header className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Appuntamenti</h1>
              <p className="text-gray-600">Gestisci i tuoi appuntamenti</p>
            </div>
            <div className="space-x-4">
              <button 
                className="bg-white px-4 py-2 rounded-lg border border-purple-600 text-purple-600 hover:bg-purple-50 flex items-center"
              >
                <Calendar className="w-5 h-5 mr-2 text-purple-600" />
                Gestisci Disponibilità
              </button>
            </div>
          </div>
        </header>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}

        {/* Filtri per intervallo di date */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-700 mr-2">Dal:</label>
              <input
                type="date"
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-700 mr-2">Al:</label>
              <input
                type="date"
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
              />
            </div>
            <button 
              className="px-3 py-2 text-purple-600 hover:bg-purple-50 rounded-md border border-purple-600"
              onClick={handleResetFilters}
            >
              Resetta filtri
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <p className="text-gray-600">Caricamento appuntamenti in corso...</p>
          </div>
        ) : appointments.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun appuntamento trovato
            </h3>
            <p className="text-gray-500">
              Non hai appuntamenti programmati al momento.
            </p>
          </div>
        ) : (
          <>
            {/* Tab navigation */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                className={`py-2 px-4 mr-2 font-medium rounded-t-lg ${
                  activeTab === 'upcoming' 
                    ? 'bg-white text-purple-600 border-b-2 border-purple-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('upcoming')}
              >
                Da completare ({filteredUpcomingAppointments.length})
              </button>
              <button
                className={`py-2 px-4 mr-2 font-medium rounded-t-lg ${
                  activeTab === 'suggested' 
                    ? 'bg-white text-purple-600 border-b-2 border-purple-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('suggested')}
              >
                Suggeriti ({filteredSuggestedAppointments.length})
              </button>
              <button
                className={`py-2 px-4 font-medium rounded-t-lg ${
                  activeTab === 'past' 
                    ? 'bg-white text-purple-600 border-b-2 border-purple-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('past')}
              >
                Appuntamenti passati ({filteredPastAppointments.length})
              </button>
            </div>

            {/* Contenuto in base al tab attivo - UPCOMING */}
            {activeTab === 'upcoming' && (
              <>
                {filteredUpcomingAppointments.length === 0 ? (
                  <div className="bg-white p-8 rounded-lg shadow text-center">
                    <p className="text-gray-600">
                      {startDateFilter || endDateFilter 
                        ? 'Nessun appuntamento da completare nell\'intervallo di date selezionato.'
                        : 'Non hai appuntamenti da completare.'}
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Paziente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data e Ora
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Stato
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredUpcomingAppointments.map((appointment) => (
                          <AppointmentRow 
                            key={appointment.id} 
                            appointment={appointment} 
                            formatDate={formatDate}
                            formatTime={formatTime}
                            getDaysRemaining={getDaysRemaining}
                            getStatusColor={getStatusColor}
                            getStatusLabel={getStatusLabel}
                            handleCompleteAppointment={handleCompleteAppointment}
                            handleCancelAppointment={handleCancelAppointment}
                            loadPatientSurvey={loadPatientSurvey}
                            handleRescheduleAppointment={handleRescheduleAppointment}
                            handleOpenUpdateModal={handleOpenUpdateModal}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Contenuto in base al tab attivo - SUGGESTED */}
            {activeTab === 'suggested' && (
              <>
                {filteredSuggestedAppointments.length === 0 ? (
                  <div className="bg-white p-8 rounded-lg shadow text-center">
                    <p className="text-gray-600">
                      {startDateFilter || endDateFilter 
                        ? 'Nessun appuntamento suggerito nell\'intervallo di date selezionato.'
                        : 'Non hai appuntamenti suggeriti.'}
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Paziente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data e Ora
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Stato
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredSuggestedAppointments.map((appointment) => (
                          <SuggestedAppointmentRow 
                            key={appointment.id} 
                            appointment={appointment} 
                            formatDate={formatDate}
                            formatTime={formatTime}
                            getDaysRemaining={getDaysRemaining}
                            getStatusColor={getStatusColor}
                            getStatusLabel={getStatusLabel}
                            handleDeleteAppointment={handleDeleteAppointment}
                            loadPatientSurvey={loadPatientSurvey}
                            handleOpenDeleteModal={handleOpenDeleteModal}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Contenuto in base al tab attivo - PAST */}
            {activeTab === 'past' && (
              <>
                {filteredPastAppointments.length === 0 ? (
                  <div className="bg-white p-8 rounded-lg shadow text-center">
                    <p className="text-gray-600">
                      {startDateFilter || endDateFilter 
                        ? 'Nessun appuntamento passato nell\'intervallo di date selezionato.'
                        : 'Non hai appuntamenti passati.'}
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Paziente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data e Ora
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Stato
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredPastAppointments.map((appointment) => (
                          <AppointmentRow 
                            key={appointment.id} 
                            appointment={appointment} 
                            formatDate={formatDate}
                            formatTime={formatTime}
                            getDaysRemaining={getDaysRemaining}
                            getStatusColor={getStatusColor}
                            getStatusLabel={getStatusLabel}
                            handleCompleteAppointment={handleCompleteAppointment}
                            handleCancelAppointment={handleCancelAppointment}
                            loadPatientSurvey={loadPatientSurvey}
                            handleRescheduleAppointment={handleRescheduleAppointment}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Modale per visualizzare le risposte al questionario */}
      {showSurveyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                Risposte al questionario - Paziente #{selectedPatient}
              </h3>
              <button 
                onClick={() => setShowSurveyModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {loadingSurvey ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Caricamento risposte in corso...</p>
              </div>
            ) : surveyError ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>{surveyError}</p>
              </div>
            ) : surveyResponses.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
                <p>Il paziente non ha ancora completato il questionario.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {surveyResponses.map(response => (
                  <div key={response.id} className="border-b pb-6">
                    <h4 className="font-semibold text-lg mb-2">
                      {response.question?.title || 'Domanda senza titolo'}
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      {response.question?.description || ''}
                    </p>
                    
                    {/* Risposta testuale */}
                    {response.text_answer && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-800">{response.text_answer}</p>
                      </div>
                    )}
                    
                    {/* Opzioni selezionate */}
                    {response.selected_options && response.selected_options.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium mb-2">Opzioni selezionate:</p>
                        <ul className="list-disc pl-5 space-y-1">
                          {response.selected_options.map(selectedOption => (
                            <li key={selectedOption.id} className="text-gray-800">
                              {selectedOption.option?.description || 'Opzione senza descrizione'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowSurveyModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modale per rifissare l'appuntamento */}
      {showRescheduleModal && selectedAppointment && (
        <RescheduleAppointmentModal
          appointment={selectedAppointment}
          onClose={() => setShowRescheduleModal(false)}
          formatDate={formatDate}
          formatTime={formatTime}
        />
      )}

      {/* Modale per eliminare l'appuntamento */}
      {showDeleteModal && appointmentToDelete && (
        <DeleteAppointmentModal
          appointment={appointmentToDelete}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={handleDeleteSuccess}
          formatDate={formatDate}
          formatTime={formatTime}
        />
      )}

      {/* Modale per modificare l'appuntamento */}
      {showUpdateModal && appointmentToUpdate && (
        <UpdateAppointmentModal
          appointment={appointmentToUpdate}
          onClose={() => setShowUpdateModal(false)}
          onSuccess={handleUpdateSuccess}
          formatDate={formatDate}
          formatTime={formatTime}
        />
)}
    </div>
  );
};

// Componente per la riga dell'appuntamento (per ridurre la duplicazione di codice)
const AppointmentRow = ({ 
  appointment, 
  formatDate, 
  formatTime, 
  getDaysRemaining,
  getStatusColor,
  getStatusLabel,
  handleCompleteAppointment,
  handleCancelAppointment,
  loadPatientSurvey,
  handleRescheduleAppointment,
  handleOpenUpdateModal
}) => {
  const daysRemaining = getDaysRemaining(appointment.date);
  let dayIndicator = '';
  
  if (daysRemaining === 0) {
    dayIndicator = ' (Oggi)';
  } else if (daysRemaining === 1) {
    dayIndicator = ' (Domani)';
  } else if (daysRemaining > 1) {
    dayIndicator = ` (tra ${daysRemaining} giorni)`;
  } else if (daysRemaining === -1) {
    dayIndicator = ' (Ieri)';
  } else {
    dayIndicator = ` (${Math.abs(daysRemaining)} giorni fa)`;
  }
  
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-purple-600" />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {appointment.patient_name && appointment.patient_name.trim() 
                ? appointment.patient_name 
                : `Paziente #${appointment.patient_id}`}
            </div>
            <div className="text-sm text-gray-500">
              {appointment.duration || '60 min'}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <Calendar className="w-5 h-5 text-gray-400 mr-2" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {formatDate(appointment.date)}
              <span className={`ml-1 text-sm font-medium ${
                daysRemaining === 0 ? 'text-red-600 font-bold' : 
                daysRemaining > 0 ? 'text-purple-600' : 'text-gray-500'
              }`}>
                {dayIndicator}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              {formatTime(appointment.time)}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">
          {appointment.service_name}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
          {getStatusLabel(appointment.status)}
        </span>
        </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-3">
          {/* Pulsante per vedere le risposte al questionario */}
          <button 
            className="text-purple-600 hover:text-purple-900"
            onClick={() => loadPatientSurvey(appointment.patient_user_id || appointment.patient_id)}
            title="Vedi risposte al questionario"
          >
            <FileText className="w-5 h-5" />
          </button>

            {/* Pulsante per modificare l'appuntamento */}
            <button 
              className="text-blue-600 hover:text-blue-900"
              onClick={() => handleOpenUpdateModal(appointment)}
              title="Modifica data/ora"
            >
              <Calendar className="w-5 h-5" />
            </button>
          
          {/* Pulsante per rifissare l'appuntamento */}
          <button 
            className="text-indigo-600 hover:text-indigo-900"
            onClick={() => handleRescheduleAppointment(appointment)}
            title="Rifissa appuntamento"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          
          {/* Aggiungi questo pulsante per il link di videoconferenza */}
          {appointment.video_conference_link && (
            <button 
              className="text-green-600 hover:text-green-900"
              onClick={() => window.open(appointment.video_conference_link, '_blank')}
              title="Apri videoconferenza"
            >
              <Video className="w-5 h-5" />
            </button>
          )}

          {appointment.status !== 'cancelled' && (
            <button 
              className="text-red-600 hover:text-red-900"
              onClick={() => handleCancelAppointment(appointment.id)}
              title="Annulla appuntamento"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Componente specifico per le righe di appuntamenti suggeriti
const SuggestedAppointmentRow = ({ 
  appointment, 
  formatDate, 
  formatTime, 
  getDaysRemaining,
  getStatusColor,
  getStatusLabel,
  handleDeleteAppointment,
  loadPatientSurvey,
  handleOpenDeleteModal
}) => {
  const daysRemaining = getDaysRemaining(appointment.date);
  let dayIndicator = '';
  
  if (daysRemaining === 0) {
    dayIndicator = ' (Oggi)';
  } else if (daysRemaining === 1) {
    dayIndicator = ' (Domani)';
  } else if (daysRemaining > 1) {
    dayIndicator = ` (tra ${daysRemaining} giorni)`;
  } else if (daysRemaining === -1) {
    dayIndicator = ' (Ieri)';
  } else {
    dayIndicator = ` (${Math.abs(daysRemaining)} giorni fa)`;
  }
  
  return (
    <tr className="hover:bg-gray-50 bg-indigo-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {appointment.patient_name && appointment.patient_name.trim() 
                ? appointment.patient_name 
                : `Paziente #${appointment.patient_id}`}
            </div>
            <div className="text-sm text-gray-500">
              {appointment.duration || '60 min'}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <Calendar className="w-5 h-5 text-gray-400 mr-2" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {formatDate(appointment.date)}
              <span className={`ml-1 text-sm font-medium ${
                daysRemaining === 0 ? 'text-red-600 font-bold' : 
                daysRemaining > 0 ? 'text-indigo-600' : 'text-gray-500'
              }`}>
                {dayIndicator}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              {formatTime(appointment.time)}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">
          {appointment.service_name}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
          {getStatusLabel(appointment.status)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-3">
          {/* Pulsante per vedere le risposte al questionario */}
          <button 
            className="text-purple-600 hover:text-purple-900"
            onClick={() => loadPatientSurvey(appointment.patient_user_id || appointment.patient_id)}
            title="Vedi risposte al questionario"
          >
            <FileText className="w-5 h-5" />
          </button>
          
            {/* Pulsante per eliminare l'appuntamento suggerito */}
            <button 
              className="text-red-600 hover:text-red-900"
              onClick={() => handleOpenDeleteModal(appointment)}
              title="Elimina appuntamento suggerito"
            >
              <Trash2 className="w-5 h-5" />
            </button>

          {/* Pulsante per eliminare l'appuntamento suggerito 
          <button 
            className="text-red-600 hover:text-red-900"
            onClick={() => handleDeleteAppointment(appointment.id)}
            title="Elimina appuntamento suggerito"
          >
            <Trash2 className="w-5 h-5" />
          </button>
          */}
        </div>
      </td>
    </tr>
  );
};

AppointmentRow.propTypes = {
  appointment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    date: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    service_name: PropTypes.string,
    status: PropTypes.string.isRequired,
    patient_name: PropTypes.string,
    patient_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    patient_user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duration: PropTypes.string,
    video_conference_link: PropTypes.string
  }).isRequired,
  formatDate: PropTypes.func.isRequired,
  formatTime: PropTypes.func.isRequired,
  getDaysRemaining: PropTypes.func.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  getStatusLabel: PropTypes.func.isRequired,
  handleCompleteAppointment: PropTypes.func.isRequired,
  handleCancelAppointment: PropTypes.func.isRequired,
  loadPatientSurvey: PropTypes.func.isRequired,
  handleRescheduleAppointment: PropTypes.func.isRequired,
  handleOpenUpdateModal: PropTypes.func.isRequired 
};

SuggestedAppointmentRow.propTypes = {
  appointment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    date: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    service_name: PropTypes.string,
    status: PropTypes.string.isRequired,
    patient_name: PropTypes.string,
    patient_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    patient_user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duration: PropTypes.string
  }).isRequired,
  formatDate: PropTypes.func.isRequired,
  formatTime: PropTypes.func.isRequired,
  getDaysRemaining: PropTypes.func.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  getStatusLabel: PropTypes.func.isRequired,
  handleDeleteAppointment: PropTypes.func.isRequired,
  loadPatientSurvey: PropTypes.func.isRequired,
  handleOpenDeleteModal: PropTypes.func.isRequired 
};

export default DoctorAppointmentsPage;